import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import {
  Plus, Trash2, ChevronUp, ChevronDown,
  Type, Image, Minus, AlignLeft, Layout as LayoutIcon,
  Eye, Code, Palette, Lock, Send, Save, ArrowLeft
} from 'lucide-react';

// ─── Block Types (content-only, no attribution blocks) ───────────────────────
const BLOCK_TYPES = [
  { type: 'text',    label: 'Text',    icon: AlignLeft },
  { type: 'header',  label: 'Heading', icon: Type },
  { type: 'image',   label: 'Image',   icon: Image },
  { type: 'button',  label: 'Button',  icon: LayoutIcon },
  { type: 'divider', label: 'Divider', icon: Minus },
  { type: 'spacer',  label: 'Spacer',  icon: Minus },
];

const defaultBlock = (type) => {
  const base = { id: `${type}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, type };
  switch (type) {
    case 'header':  return { ...base, text: 'Your Subject Here', size: '24', color: '#ffffff', bg: '#111111', align: 'left', bold: true };
    case 'text':    return { ...base, text: 'Write your message to ward residents here...', color: '#cccccc', bg: '#0d0d0d', size: '15', align: 'left' };
    case 'image':   return { ...base, url: '', alt: '', width: '100%', link: '' };
    case 'button':  return { ...base, text: 'Learn More', url: 'https://cyqr.ca', bg: '#C5A52A', color: '#000000', align: 'center' };
    case 'divider': return { ...base, color: '#2a2a2a', thickness: '1' };
    case 'spacer':  return { ...base, height: '16' };
    default:        return base;
  }
};

// ─── Locked Attribution Block (top) ──────────────────────────────────────────
const AttributionBanner = ({ name, ward }) => (
  <div style={{ background: '#0a1a0a', borderBottom: '3px solid #16a34a', padding: '16px 24px', fontFamily: 'Arial, sans-serif' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        background: '#16a34a', color: '#fff', fontWeight: 'bold', fontSize: '10px',
        padding: '3px 8px', letterSpacing: '2px', fontFamily: 'monospace', flexShrink: 0
      }}>
        OFFICIAL COUNCILLOR CORRESPONDENCE
      </div>
      <div style={{ color: '#86efac', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>
        From <strong style={{ color: '#ffffff' }}>{name || 'Your Councillor'}</strong> — {ward || 'Your Ward'}
      </div>
    </div>
  </div>
);

// ─── Locked Disclaimer Footer ─────────────────────────────────────────────────
const DisclaimerFooter = ({ name, ward }) => (
  <div style={{ background: '#0a0a0a', borderTop: '1px solid #1a2a1a', padding: '16px 24px', fontFamily: 'Arial, sans-serif' }}>
    <p style={{ color: '#4ade80', fontSize: '11px', fontFamily: 'monospace', margin: '0 0 6px', letterSpacing: '1px' }}>
      ⚠ IMPORTANT NOTICE — PLEASE READ
    </p>
    <p style={{ color: '#6b7280', fontSize: '12px', lineHeight: '1.6', margin: '0' }}>
      This email is a <strong style={{ color: '#9ca3af' }}>direct communication from {name || 'your elected councillor'}</strong>,
      your elected representative for {ward || 'your ward'}.
      This message represents the <strong style={{ color: '#9ca3af' }}>personal views and correspondence of the councillor</strong>{' '}
      and does <strong style={{ color: '#f87171' }}>NOT</strong> represent the views, positions, or endorsement of{' '}
      <strong style={{ color: '#9ca3af' }}>CYQR.CA</strong> or its team.
      CYQR.CA is a civic engagement platform that facilitates communication between residents and their elected officials.
    </p>
    <p style={{ color: '#374151', fontSize: '11px', fontFamily: 'monospace', margin: '10px 0 0' }}>
      You received this because you subscribed to ward updates at cyqr.ca ·{' '}
      <a href="https://cyqr.ca/unsubscribe" style={{ color: '#4b5563' }}>Unsubscribe</a>
    </p>
  </div>
);

// ─── Block Preview (content blocks only) ─────────────────────────────────────
const BlockPreview = ({ block }) => {
  const s = { fontFamily: 'Arial, sans-serif' };
  switch (block.type) {
    case 'header':  return <div style={{ ...s, background: block.bg, color: block.color, fontSize: `${block.size}px`, fontWeight: block.bold ? 'bold' : 'normal', textAlign: block.align, padding: '14px 24px' }}>{block.text}</div>;
    case 'text':    return <div style={{ ...s, background: block.bg, color: block.color, fontSize: `${block.size}px`, textAlign: block.align, padding: '10px 24px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{block.text}</div>;
    case 'image':   return (
      <div style={{ background: '#0d0d0d', textAlign: 'center', padding: '8px 24px' }}>
        {block.url ? <img src={block.url} alt={block.alt} style={{ maxWidth: block.width || '100%', height: 'auto' }} onError={e => { e.target.style.opacity = 0.3; }} />
          : <div style={{ border: '2px dashed #2a2a2a', padding: '24px', color: '#555', fontSize: '12px', fontFamily: 'monospace' }}>Image URL not set</div>}
      </div>
    );
    case 'button':  return <div style={{ background: '#0d0d0d', textAlign: block.align || 'center', padding: '14px 24px' }}><span style={{ display: 'inline-block', background: block.bg, color: block.color, padding: '10px 24px', fontWeight: 'bold', fontSize: '14px', fontFamily: 'Arial, sans-serif', cursor: 'default' }}>{block.text}</span></div>;
    case 'divider': return <div style={{ background: '#0d0d0d', padding: '4px 24px' }}><div style={{ height: `${block.thickness || 1}px`, background: block.color }} /></div>;
    case 'spacer':  return <div style={{ height: `${block.height}px`, background: '#0d0d0d' }} />;
    default:        return null;
  }
};

// ─── HTML generators ──────────────────────────────────────────────────────────
const blockToHtml = (block) => {
  switch (block.type) {
    case 'header':  return `<tr><td style="background:${block.bg};padding:14px 24px;text-align:${block.align};font-family:Arial,sans-serif;font-size:${block.size}px;font-weight:${block.bold ? 'bold' : 'normal'};color:${block.color};">${block.text}</td></tr>`;
    case 'text':    return `<tr><td style="background:${block.bg};padding:10px 24px;text-align:${block.align};font-family:Arial,sans-serif;font-size:${block.size}px;color:${block.color};line-height:1.6;">${block.text.replace(/\n/g, '<br>')}</td></tr>`;
    case 'image':   return block.url ? `<tr><td style="background:#0d0d0d;padding:8px 24px;text-align:center;">${block.link ? `<a href="${block.link}">` : ''}<img src="${block.url}" alt="${block.alt || ''}" width="${block.width === '100%' ? '100%' : block.width}" style="max-width:100%;height:auto;display:block;margin:0 auto;">${block.link ? '</a>' : ''}</td></tr>` : '';
    case 'button':  return `<tr><td style="background:#0d0d0d;padding:14px 24px;text-align:${block.align};"><a href="${block.url}" style="display:inline-block;background:${block.bg};color:${block.color};padding:10px 24px;text-decoration:none;font-weight:bold;font-size:14px;font-family:Arial,sans-serif;">${block.text}</a></td></tr>`;
    case 'divider': return `<tr><td style="background:#0d0d0d;padding:4px 24px;"><div style="height:${block.thickness || 1}px;background:${block.color};"></div></td></tr>`;
    case 'spacer':  return `<tr><td style="background:#0d0d0d;height:${block.height}px;"></td></tr>`;
    default:        return '';
  }
};

const generateHtml = (blocks, subject, name, ward) => `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${subject}</title>
</head>
<body style="margin:0;padding:0;background:#111;font-family:Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#111;">
<tr><td align="center" style="padding:20px 0;">
<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:#0d0d0d;">

<!-- ATTRIBUTION HEADER — DO NOT REMOVE -->
<tr><td style="background:#0a1a0a;border-bottom:3px solid #16a34a;padding:16px 24px;">
<table width="100%" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="padding-right:12px;white-space:nowrap;"><span style="background:#16a34a;color:#fff;font-weight:bold;font-size:10px;padding:3px 8px;letter-spacing:2px;font-family:monospace;">OFFICIAL COUNCILLOR CORRESPONDENCE</span></td>
<td style="font-size:13px;font-family:Arial,sans-serif;color:#86efac;">From <strong style="color:#ffffff;">${name}</strong> &mdash; ${ward}</td>
</tr>
</table>
</td></tr>

${blocks.map(blockToHtml).filter(Boolean).join('\n')}

<!-- DISCLAIMER FOOTER — DO NOT REMOVE -->
<tr><td style="background:#0a0a0a;border-top:1px solid #1a2a1a;padding:16px 24px;">
<p style="color:#4ade80;font-size:11px;font-family:monospace;margin:0 0 6px;letter-spacing:1px;">&#9888; IMPORTANT NOTICE &mdash; PLEASE READ</p>
<p style="color:#6b7280;font-size:12px;line-height:1.6;margin:0;">This email is a <strong style="color:#9ca3af;">direct communication from ${name}</strong>, your elected representative for ${ward}. This message represents the <strong style="color:#9ca3af;">personal views and correspondence of the councillor</strong> and does <strong style="color:#f87171;">NOT</strong> represent the views, positions, or endorsement of <strong style="color:#9ca3af;">CYQR.CA</strong> or its team. CYQR.CA is a civic engagement platform that facilitates communication between residents and their elected officials.</p>
<p style="color:#374151;font-size:11px;font-family:monospace;margin:10px 0 0;">You received this because you subscribed to ward updates at cyqr.ca &middot; <a href="https://cyqr.ca/unsubscribe" style="color:#4b5563;">Unsubscribe</a></p>
</td></tr>

</table>
</td></tr>
</table>
</body>
</html>`;

// ─── Block Editor ─────────────────────────────────────────────────────────────
const BlockEditor = ({ block, onChange }) => {
  const field = (key, label, type = 'text', opts = {}) => (
    <div key={key}>
      <label className="block font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea value={block[key] || ''} onChange={e => onChange(key, e.target.value)} rows={4}
          className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-xs p-2 font-mono resize-y focus:border-[#C5A52A] outline-none" />
      ) : type === 'select' ? (
        <select value={block[key] || ''} onChange={e => onChange(key, e.target.value)}
          className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-xs p-2 font-mono focus:border-[#C5A52A] outline-none">
          {opts.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === 'color' ? (
        <div className="flex items-center gap-2">
          <input type="color" value={block[key] || '#ffffff'} onChange={e => onChange(key, e.target.value)}
            className="w-8 h-8 cursor-pointer border-0 bg-transparent p-0" />
          <span className="font-mono text-xs text-gray-400">{block[key]}</span>
        </div>
      ) : type === 'checkbox' ? (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!block[key]} onChange={e => onChange(key, e.target.checked)}
            className="w-4 h-4 accent-[#C5A52A]" />
          <span className="text-xs text-gray-400">{opts.checkLabel}</span>
        </label>
      ) : (
        <input type={type} value={block[key] || ''} onChange={e => onChange(key, e.target.value)}
          className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-xs p-2 font-mono focus:border-[#C5A52A] outline-none" />
      )}
    </div>
  );

  const alignOpts = { options: [{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }] };

  return (
    <div className="space-y-3 p-4">
      {block.type === 'header' && <>
        {field('text',  'Heading Text', 'textarea')}
        {field('size',  'Font Size (px)', 'number')}
        {field('color', 'Text Color', 'color')}
        {field('bg',    'Background Color', 'color')}
        {field('align', 'Alignment', 'select', alignOpts)}
        {field('bold',  'Bold', 'checkbox', { checkLabel: 'Bold text' })}
      </>}
      {block.type === 'text' && <>
        {field('text',  'Body Text', 'textarea')}
        {field('size',  'Font Size (px)', 'number')}
        {field('color', 'Text Color', 'color')}
        {field('bg',    'Background Color', 'color')}
        {field('align', 'Alignment', 'select', alignOpts)}
      </>}
      {block.type === 'image' && <>
        {field('url',   'Image URL', 'text')}
        {field('alt',   'Alt Text', 'text')}
        {field('link',  'Click-through URL (optional)', 'text')}
        {field('width', 'Width (px or %)', 'text')}
      </>}
      {block.type === 'button' && <>
        {field('text',  'Button Label', 'text')}
        {field('url',   'Link URL', 'text')}
        {field('bg',    'Button Color', 'color')}
        {field('color', 'Text Color', 'color')}
        {field('align', 'Alignment', 'select', alignOpts)}
      </>}
      {block.type === 'divider' && <>
        {field('color',     'Line Color', 'color')}
        {field('thickness', 'Thickness (px)', 'number')}
      </>}
      {block.type === 'spacer' && <>
        {field('height', 'Height (px)', 'number')}
      </>}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const CouncillorEmailBlastCompose = () => {
  const navigate = useNavigate();
  const { blastId } = useParams();
  const isEdit = !!blastId;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [subject, setSubject] = useState('');
  const [blocks, setBlocks] = useState([defaultBlock('text')]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('design');

  // Load councillor profile + existing blast if editing
  useEffect(() => {
    const init = async () => {
      try {
        const profileRes = await api.get('/councillors/my-profile');
        const c = profileRes.data;
        setProfile(c);

        if (isEdit) {
          const blastsRes = await api.get('/councillors/email-blasts');
          const blast = blastsRes.data.find(b => b.id === blastId);
          if (!blast) {
            toast.error('Blast not found');
            navigate('/councillor/dashboard');
            return;
          }
          if (blast.status !== 'draft') {
            toast.error('Only draft blasts can be edited');
            navigate('/councillor/dashboard');
            return;
          }
          setSubject(blast.subject || '');
          // Try to load saved blocks from html_content metadata
          if (blast.blocks_json) {
            try { setBlocks(JSON.parse(blast.blocks_json)); } catch { /* fallback */ }
          }
        }
      } catch (err) {
        toast.error('Failed to load profile');
        navigate('/councillor/dashboard');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [blastId, isEdit, navigate]);

  const councillorName = profile?.name || 'Councillor';
  const councillorWard = profile?.ward || 'Your Ward';
  const html = profile ? generateHtml(blocks, subject, councillorName, councillorWard) : '';

  const addBlock = (type) => {
    const nb = defaultBlock(type);
    setBlocks(b => [...b, nb]);
    setSelected(nb.id);
  };

  const updateBlock = useCallback((id, key, value) => {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, [key]: value } : b));
  }, []);

  const removeBlock = (id) => {
    setBlocks(b => b.filter(bl => bl.id !== id));
    if (selected === id) setSelected(null);
  };

  const moveBlock = (id, dir) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === id);
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const selectedBlock = blocks.find(b => b.id === selected);

  const buildPayload = () => ({
    subject: subject.trim(),
    body: html,
    blocks_json: JSON.stringify(blocks),
  });

  const handleSaveDraft = async () => {
    if (!subject.trim()) { toast.error('Subject line is required'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.put(`/councillors/email-blast/${blastId}`, payload);
        toast.success('Draft saved!');
      } else {
        await api.post('/councillors/email-blast', payload);
        toast.success('Draft saved!');
        navigate('/councillor/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!subject.trim()) { toast.error('Subject line is required'); return; }
    if (blocks.length === 0) { toast.error('Add at least one content block'); return; }
    setSubmitting(true);
    try {
      // Save first, then submit
      const payload = buildPayload();
      let id = blastId;
      if (!isEdit) {
        const res = await api.post('/councillors/email-blast', payload);
        id = res.data?.id;
      } else {
        await api.put(`/councillors/email-blast/${blastId}`, payload);
      }
      await api.post(`/councillors/email-blast/${id}/submit`);
      toast.success('Submitted for admin review!');
      navigate('/councillor/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500 mx-auto mb-4" />
            <p className="text-gray-500 font-mono text-sm">LOADING COMPOSER...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#0D0D0D] text-white">

        {/* Top bar */}
        <div className="bg-[#111] border-b border-[#1E1E1E] sticky top-0 z-40">
          <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/councillor/dashboard')}
                className="text-gray-400 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <h1 className="font-mono text-sm text-white tracking-wider">
                {isEdit ? 'EDIT EMAIL BLAST' : 'NEW EMAIL BLAST'}
              </h1>
              <span className="font-mono text-[10px] text-gray-600 bg-[#0d0d0d] border border-[#2a2a2a] px-2 py-0.5">
                {councillorName} · {councillorWard}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Mode tabs */}
              {[['design', 'Design'], ['preview', 'Preview'], ['code', 'HTML Code']].map(([m, label]) => (
                <button key={m} onClick={() => setMode(m)}
                  className={`px-3 py-1.5 font-mono text-xs transition-colors ${mode === m ? 'bg-green-500 text-black font-bold' : 'text-gray-400 hover:text-white border border-[#2a2a2a]'}`}>
                  {label}
                </button>
              ))}
              <div className="w-px h-5 bg-[#2a2a2a]" />
              <button onClick={handleSaveDraft} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs bg-[#2a2a2a] text-gray-300 hover:text-white transition-colors disabled:opacity-50">
                <Save className="w-3 h-3" />{saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                className="flex items-center gap-1.5 px-4 py-1.5 font-mono text-xs bg-green-600 hover:bg-green-500 text-white font-bold transition-colors disabled:opacity-50">
                <Send className="w-3 h-3" />{submitting ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-4 py-6">

          {/* Subject line */}
          <div className="bg-[#111] border border-[#1E1E1E] p-4 mb-4">
            <label className="block font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-1">Subject Line *</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="e.g. Ward 4 Community Update — April 2025"
              className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-sm p-2 font-mono focus:border-green-500 outline-none"
            />
          </div>

          {/* Locked attribution notice */}
          <div className="bg-green-900/10 border border-green-800/40 p-3 mb-4 flex items-start gap-3">
            <Lock className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-mono text-[11px] text-green-400 font-bold uppercase tracking-wider mb-0.5">Locked Attribution Blocks</p>
              <p className="text-gray-400 text-xs">
                A <strong className="text-green-400">councillor identification header</strong> and a <strong className="text-green-400">mandatory disclaimer footer</strong> are automatically
                attached to your email. These blocks cannot be edited or removed — they ensure recipients know this communication
                is from <strong className="text-white">{councillorName}</strong> personally and <strong className="text-red-400">not from CYQR.CA</strong>.
              </p>
            </div>
          </div>

          {/* ── DESIGN mode ── */}
          {mode === 'design' && (
            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_280px] gap-4">

              {/* LEFT: Add blocks */}
              <div className="bg-[#111] border border-[#1E1E1E] p-4">
                <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-3">Add Content Block</p>
                <div className="space-y-1">
                  {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
                    <button key={type} onClick={() => addBlock(type)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-gray-400 hover:text-white hover:bg-[#1a1a1a] transition-colors font-mono">
                      <Plus className="w-3 h-3 text-green-500 flex-shrink-0" />
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      {label}
                    </button>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-[#1E1E1E]">
                  <p className="font-mono text-[10px] text-gray-600 uppercase tracking-wider mb-2">Locked (auto-included)</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-900/10 border border-green-800/30 rounded">
                      <Lock className="w-3 h-3 text-green-500" />
                      <span className="text-green-400 font-mono text-[10px]">Attribution Header</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-900/10 border border-green-800/30 rounded">
                      <Lock className="w-3 h-3 text-green-500" />
                      <span className="text-green-400 font-mono text-[10px]">Disclaimer Footer</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* CENTRE: Canvas */}
              <div className="space-y-1">
                {/* Locked top block */}
                <div className="border-2 border-green-800/50 opacity-80 cursor-not-allowed">
                  <div className="flex items-center justify-between px-2 py-1 bg-green-900/20">
                    <span className="font-mono text-[10px] text-green-500 uppercase flex items-center gap-1.5">
                      <Lock className="w-3 h-3" /> ATTRIBUTION HEADER — LOCKED
                    </span>
                  </div>
                  <AttributionBanner name={councillorName} ward={councillorWard} />
                </div>

                {/* Editable content blocks */}
                {blocks.length === 0 && (
                  <div className="border-2 border-dashed border-[#2a2a2a] p-12 text-center">
                    <p className="text-gray-600 font-mono text-sm">Add content blocks from the left panel</p>
                  </div>
                )}
                {blocks.map((block, idx) => (
                  <div key={block.id}
                    onClick={() => setSelected(block.id)}
                    className={`border-2 cursor-pointer transition-all ${selected === block.id ? 'border-green-500' : 'border-transparent hover:border-[#2a2a2a]'}`}>
                    <div className={`flex items-center justify-between px-2 py-1 ${selected === block.id ? 'bg-green-500/10' : 'bg-[#111]'}`}>
                      <span className="font-mono text-[10px] text-gray-600 uppercase">{block.type}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={e => { e.stopPropagation(); moveBlock(block.id, -1); }} disabled={idx === 0}
                          className="p-0.5 text-gray-600 hover:text-white disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                        <button onClick={e => { e.stopPropagation(); moveBlock(block.id, 1); }} disabled={idx === blocks.length - 1}
                          className="p-0.5 text-gray-600 hover:text-white disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                        <button onClick={e => { e.stopPropagation(); removeBlock(block.id); }}
                          className="p-0.5 text-red-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                    <BlockPreview block={block} />
                  </div>
                ))}

                {/* Locked bottom block */}
                <div className="border-2 border-green-800/50 opacity-80 cursor-not-allowed">
                  <div className="flex items-center justify-between px-2 py-1 bg-green-900/20">
                    <span className="font-mono text-[10px] text-green-500 uppercase flex items-center gap-1.5">
                      <Lock className="w-3 h-3" /> DISCLAIMER FOOTER — LOCKED
                    </span>
                  </div>
                  <DisclaimerFooter name={councillorName} ward={councillorWard} />
                </div>
              </div>

              {/* RIGHT: Editor */}
              <div className="bg-[#111] border border-[#1E1E1E]">
                {selectedBlock ? (
                  <>
                    <div className="px-4 py-3 border-b border-[#1E1E1E] flex items-center gap-2">
                      <Palette className="w-4 h-4 text-green-500" />
                      <span className="font-mono text-xs text-white uppercase tracking-wider">Edit {selectedBlock.type}</span>
                    </div>
                    <BlockEditor
                      block={selectedBlock}
                      onChange={(key, value) => updateBlock(selectedBlock.id, key, value)}
                    />
                  </>
                ) : (
                  <div className="p-6 text-center">
                    <Palette className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                    <p className="text-gray-600 font-mono text-xs">Click a content block to edit it</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── PREVIEW mode ── */}
          {mode === 'preview' && (
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-8">
              <div className="max-w-[600px] mx-auto">
                <div className="bg-[#2a2a2a] rounded-t px-4 py-2 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/60" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                    <div className="w-3 h-3 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-gray-500 font-mono text-xs ml-2 truncate">{subject || '(no subject)'}</span>
                </div>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
              </div>
              <p className="text-center text-gray-600 font-mono text-[10px] mt-4">This is an approximation. Actual rendering varies by email client.</p>
            </div>
          )}

          {/* ── HTML CODE mode ── */}
          {mode === 'code' && (
            <div className="bg-[#0d0d0d] border border-[#1E1E1E]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E1E1E]">
                <div>
                  <p className="font-mono text-xs text-white">Generated HTML — for admin reference</p>
                  <p className="font-mono text-[10px] text-gray-600 mt-0.5">This is the exact HTML that will be reviewed and sent by the admin.</p>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(html); toast.success('Copied!'); }}
                  className="px-3 py-1.5 font-mono text-xs bg-[#2a2a2a] text-gray-300 hover:text-white transition-colors">
                  Copy HTML
                </button>
              </div>
              <pre className="p-4 text-xs text-green-400/70 font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">{html}</pre>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default CouncillorEmailBlastCompose;
